import { useEffect, useState } from "react";
import { soloApi } from "@/lib/api";
import { AGENTS } from "@/lib/agents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, AlertTriangle, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Accountant() {
  const a = AGENTS.accountant;
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [cpas, setCpas] = useState([]);
  const [form, setForm] = useState({ kind: "expense", amount: "", description: "" });

  const load = () => {
    soloApi.transactions().then(({ data }) => setTxns(data));
    soloApi.accountantSummary().then(({ data }) => setSummary(data));
    soloApi.cpas().then(({ data }) => setCpas(data));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.amount || !form.description) return;
    await soloApi.createTransaction({ kind: form.kind, amount: parseFloat(form.amount), description: form.description });
    setForm({ kind: "expense", amount: "", description: "" });
    toast.success("Transaction added");
    load();
  };

  return (
    <div className="p-8 pb-16 min-h-screen">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-2xl overflow-hidden bg-indigo-50 flex-shrink-0">
            <img src={a.avatar} alt="Ada" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-indigo-500">Accountant · calm mode</div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Fredoka' }}>Ada</h1>
            <p className="text-slate-600 text-sm">Tracks money. Categorizes on the fly. Never files anything — that goes to a human CPA.</p>
          </div>

          <Dialog>
            <DialogTrigger asChild>
              <Button data-testid="accountant-add-txn-btn" className="ml-auto rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
                <Plus className="w-4 h-4 mr-1" /> Add transaction
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="text-slate-900">New transaction</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="txn-kind" className="border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="txn-amount" type="number" placeholder="Amount ($)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                <Input data-testid="txn-description" placeholder="What is it? (Ada categorizes)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <Button data-testid="txn-save" onClick={add} className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <KPI icon={TrendingUp} label="Income" value={`$${(summary?.income ?? 0).toFixed(2)}`} tone="green" tid="kpi-income" />
          <KPI icon={TrendingDown} label="Expenses" value={`$${(summary?.expense ?? 0).toFixed(2)}`} tone="slate" tid="kpi-expense" />
          <KPI icon={Wallet} label="Net" value={`$${(summary?.net ?? 0).toFixed(2)}`} tone={(summary?.net ?? 0) >= 0 ? "green" : "red"} tid="kpi-net" />
          <KPI
            icon={AlertTriangle}
            label="Runway"
            value={summary?.runway_months == null ? "—" : `${summary.runway_months} mo`}
            tone={summary?.flag_runway ? "red" : "slate"}
            tid="kpi-runway"
          />
        </div>

        {summary?.flag_runway && (
          <div className="card-calm p-4 mb-6 border-l-4 border-l-red-500" data-testid="runway-flag">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-slate-900">Runway below 3 months.</div>
                <p className="text-sm text-slate-600">Consider raising revenue or trimming expenses. Ada can prep a variance summary.</p>
              </div>
            </div>
          </div>
        )}

        {/* Transactions */}
        <div className="card-calm p-6 mb-6" data-testid="txns-list">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Fredoka' }}>Transactions</h2>
            <span className="text-xs text-slate-500">{txns.length} total</span>
          </div>
          {txns.length === 0 ? (
            <p className="text-slate-500 text-sm">No transactions yet. Add one above.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {txns.map((t) => (
                <div key={t.txn_id} className="py-3 flex items-center gap-4" data-testid={`txn-${t.txn_id}`}>
                  <div className="w-2 h-2 rounded-full" style={{ background: t.kind === "income" ? "#10b981" : "#94a3b8" }} />
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{t.description}</div>
                    <div className="text-xs text-slate-500">{t.category} · {new Date(t.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className={"font-mono font-semibold " + (t.kind === "income" ? "text-emerald-600" : "text-slate-700")}>
                    {t.kind === "income" ? "+" : "−"}${t.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CPA marketplace */}
        <div className="card-calm p-6" data-testid="cpa-marketplace">
          <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Fredoka' }}>Human CPA handoff</h2>
          <p className="text-sm text-slate-600 mb-4">When it's time to file, a certified human takes over. Ada prepares the packet.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {cpas.map((c) => (
              <div key={c.id} className="border border-slate-200 rounded-xl p-4 hover:border-indigo-400 transition-colors" data-testid={`cpa-${c.id}`}>
                <div className="font-semibold text-slate-900">{c.name}</div>
                <div className="text-xs text-slate-500 mt-1">{c.focus}</div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs text-slate-600">★ {c.rating}</div>
                  <div className="text-sm font-semibold text-slate-900">from ${c.starts_at}</div>
                </div>
                <Button data-testid={`cpa-request-${c.id}`} onClick={() => toast.success("Request sent. They'll reach out.")} className="w-full mt-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-sm">Request handoff</Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, tone, tid }) {
  const cls = {
    green: "text-emerald-600 bg-emerald-50",
    slate: "text-slate-600 bg-slate-50",
    red: "text-red-600 bg-red-50",
  }[tone];
  return (
    <div className="card-calm p-4" data-testid={tid}>
      <div className={"w-9 h-9 rounded-lg flex items-center justify-center mb-2 " + cls}><Icon className="w-4 h-4" /></div>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Fredoka' }}>{value}</div>
    </div>
  );
}
